import { useContext } from "react";

import Box from "@/components/Box";
import LabReports from "@/components/LabReports";
import Loading from "@/components/Loading";
import ResultExplanation from "@/components/ResultExplanation";
import CardListContext from "@/contexts/CardListContext";
import RelatedResultContext from "@/contexts/RelatedResultContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { useDataLoader } from "@/hooks/useDataLoader";
import useListControls from "@/hooks/useListControls";
import { PanelBoxV2 } from "@/tpo/Boxes";
import { NoItems } from "@/tpo/EmptySearch";
import InfiniteScroll, { InfiniteScrollContext } from "@/tpo/InfiniteScroll";
import SortMenu from "@/tpo/SortMenu";
import { Search } from "@/tpo/TextInput";
import { ViewerContext } from "@/tpo/Viewer";

import DatapointChartCard from "./DatapointChartCard";
import RelatedReportsSection from "./RelatedReportsSection";
import ReleaseResultsToClient from "./ReleaseResultsToClient";
import { LAB_REPORTS_QUERY, TEST_BIOMARKERS_CONNECTION_QUERY } from "./graphql";

const PAGE_SIZE = 10;

export default function NonGenesBiomarkers({ showFullExplanation, userTest, clientId }) {
  const ctx = useContext(CardListContext);
  const viewerContext = useContext(ViewerContext);
  const testMode = new URLSearchParams(location.search).has("testMode");

  const controls = useListControls({
    sortOptions: [
      {
        label: "Biomarkers: A - Z",
        value: "datapoint__category,datapoint__name"
      },
      {
        label: "Biomarkers: Z - A",
        value: "-datapoint__category,-datapoint__name"
      },
      {
        label: "Biomarkers: Low to High",
        value: "-normalised_order"
      },
      {
        label: "Biomarkers: High to Low",
        value: "normalised_order"
      }
    ]
  });

  const { organisation } = useOrganisationContext();

  const { data, loading, fetchMore } = useDataLoader({
    query: TEST_BIOMARKERS_CONNECTION_QUERY,
    variables: {
      userTestId: userTest.id,
      userId: viewerContext?.userId,
      first: PAGE_SIZE,
      after: "",
      orderBy: controls.sort,
      search: controls.debouncedSearch,
      testMode: !!testMode,
      organisationId: organisation?.id
    },
    skip: !organisation
  });

  const userDatapoints = data?.userDatapointConnections?.edges.map(edge => edge.node) || [];

  return (
    <>
      <RelatedReportsSection userTest={userTest} />
      <ReleaseResultsToClient userTest={userTest} />
      <LabReports
        variables={{
          userId: clientId,
          organisationId: organisation?.id,
          forPractitioner: true
        }}
        userTest={userTest}
        userId={clientId}
        query={LAB_REPORTS_QUERY}
        organisationId={organisation?.id}
      />
      <ResultExplanation />
      <PanelBoxV2 maxWidth={[1020]}>
        <Box
          display="flex"
          flexDirection={["column", "row", "row"]}
          gap={20}
          justifyContent={["center", "flex-end", "flex-end"]}
        >
          <Search value={controls.search} onChange={controls.setSearch} />
          <Box display="flex" justifyContent="flex-end">
            <SortMenu
              value={controls.sort}
              setValue={controls.setSort}
              open={controls.sortMenuOpen}
              setOpen={controls.setSortMenuOpen}
              options={controls.sortOptions}
              placeholder="Sort By"
            />
          </Box>
        </Box>
      </PanelBoxV2>
      <PanelBoxV2
        maxWidth={[1020]}
        inner={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          position: "relative",
          minHeight: 1000
        }}
      >
        {userDatapoints?.length ? (
          <InfiniteScroll
            loader={<Loading />}
            hasMore={data?.userDatapointConnections?.pageInfo?.hasNextPage}
            loading={loading}
            next={() => {
              const endCursor = data?.userDatapointConnections?.pageInfo?.endCursor;
              if (endCursor) {
                fetchMore({
                  variables: {
                    after: endCursor,
                    first: 10
                  }
                });
              }
            }}
            items={userDatapoints}
          >
            <RelatedResultContext.Provider
              value={{
                openModal: result => {
                  ctx.viewCard({
                    type: "results",
                    item: result
                  });
                }
              }}
            >
              <InfiniteScrollContext.Consumer>
                {({ itemsList, setBottomElement }) => (
                  <>
                    {itemsList.slice(0, -1).map(item => (
                      <DatapointChartCard
                        userDatapoint={item}
                        key={item.id}
                        onClick={() => {
                          ctx?.viewCard({
                            type: "datapoints",
                            item
                          });
                        }}
                        includeRelatedResults={false}
                      />
                    ))}
                    {itemsList.slice(-1).map(item => (
                      <DatapointChartCard
                        userDatapoint={item}
                        key={item.id}
                        onClick={() => {
                          ctx?.viewCard({
                            type: "datapoints",
                            item
                          });
                        }}
                        includeRelatedResults={false}
                        ref={setBottomElement}
                      />
                    ))}
                  </>
                )}
              </InfiniteScrollContext.Consumer>
            </RelatedResultContext.Provider>
          </InfiniteScroll>
        ) : (
          <NoItems search={controls.search} loading={loading} />
        )}
      </PanelBoxV2>
    </>
  );
}

NonGenesBiomarkers.defaultProps = {
  showFullExplanation: true
};
